/**
 * New Appointment Page
 * Multi-step appointment creation wizard.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { AppointmentForm } from "@/components/scheduling/appointments/AppointmentForm";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, Skeleton } from "@jourfx/ui";

export const Route = createFileRoute("/_authenticated/appointments/new")({
  component: NewAppointmentPage,
});

function NewAppointmentPage() {
  const { t } = useTranslation("appointments");
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();

  const canCreate = user ? ["gutachter", "assistent", "administrator", "super_admin"].includes(user.role) : false;

  useEffect(() => {
    if (!isLoading && user && !canCreate) {
      navigate({ to: "/appointments" });
    }
  }, [isLoading, user, canCreate, navigate]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canCreate) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Sie haben keine Berechtigung, Termine zu erstellen.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <Link
          to="/appointments"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("backToList")}
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">{t("new.title")}</h1>
        <p className="text-muted-foreground">
          {t("new.subtitle")}
        </p>
      </div>

      <AppointmentForm />
    </div>
  );
}
